import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InfoNote } from "@/components/shared";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const TagEditor = ({ label, values, onChange, placeholder, testId }) => {
  const [v, setV] = useState("");
  const add = () => {
    const t = v.trim();
    if (t && !values.includes(t)) onChange([...values, t]);
    setV("");
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {values.map((x) => (
          <Badge key={x} variant="outline" className="gap-1 pr-1 text-xs">
            {x}
            <button onClick={() => onChange(values.filter((y) => y !== x))} className="rounded-full p-0.5 hover:bg-muted"><X className="h-3 w-3" /></button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} className="h-8 text-xs" data-testid={testId} />
        <Button type="button" size="sm" variant="outline" className="h-8" onClick={add}><Plus className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
};

export default function SettingsPage() {
  const { user, setUser, activeWorkspace, activeWebsite, loadWorkspaces } = useApp();
  const navigate = useNavigate();
  const [strategy, setStrategy] = useState(null);
  const [profile, setProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState("");
  const [account, setAccount] = useState({ name: "", current_password: "", new_password: "" });
  const [invite, setInvite] = useState({ email: "", role: "member" });
  const [delOpen, setDelOpen] = useState(null); // 'website' | 'workspace'
  const [confirmName, setConfirmName] = useState("");

  useEffect(() => {
    setAccount((a) => ({ ...a, name: user?.name || "" }));
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        if (activeWebsite) {
          const [{ data: s }, { data: p }] = await Promise.all([
            api.get(`/websites/${activeWebsite.id}/strategy`),
            api.get(`/websites/${activeWebsite.id}/business-profile`),
          ]);
          setStrategy(s);
          setProfile(p);
        }
        if (activeWorkspace) {
          const { data: m } = await api.get(`/workspaces/${activeWorkspace.id}/members`);
          setMembers(m);
        }
      } catch (e) { /* viewer may lack access to some */ }
    })();
  }, [activeWebsite, activeWorkspace]);

  const save = async (key, fn, success) => {
    setBusy(key);
    try { await fn(); toast.success(success); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const saveStrategy = () => save("strategy", async () => {
    const { data } = await api.put(`/websites/${activeWebsite.id}/strategy`, strategy);
    setStrategy(data);
  }, "SEO strategy saved");

  const saveProfile = () => save("profile", async () => {
    const { data } = await api.put(`/websites/${activeWebsite.id}/business-profile`, profile);
    setProfile(data);
  }, "Business profile saved");

  const saveAccount = () => save("account", async () => {
    const body = { name: account.name };
    if (account.new_password) { body.current_password = account.current_password; body.new_password = account.new_password; }
    const { data } = await api.put("/auth/me", body);
    setUser(data);
    setAccount({ name: data.name, current_password: "", new_password: "" });
  }, "Account updated");

  const addMember = () => save("invite", async () => {
    await api.post(`/workspaces/${activeWorkspace.id}/members`, invite);
    const { data: m } = await api.get(`/workspaces/${activeWorkspace.id}/members`);
    setMembers(m);
    setInvite({ email: "", role: "member" });
  }, "Member added");

  const doDelete = () => save("delete", async () => {
    if (delOpen === "website") {
      await api.delete(`/websites/${activeWebsite.id}`, { data: { confirm_name: confirmName } });
      await loadWorkspaces(activeWorkspace.id);
      navigate("/dashboard");
    } else {
      await api.delete(`/workspaces/${activeWorkspace.id}`, { data: { confirm_name: confirmName } });
      await loadWorkspaces();
      navigate("/onboarding");
    }
    setDelOpen(null);
    setConfirmName("");
  }, delOpen === "website" ? "Website deleted" : "Workspace deleted");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Settings</h1>
        <p className="text-xs text-muted-foreground">Workspace: {activeWorkspace?.name} · Website: {activeWebsite?.domain || "none"}</p>
      </div>

      <Tabs defaultValue="strategy">
        <TabsList className="flex-wrap">
          <TabsTrigger value="strategy" data-testid="settings-tab-strategy">SEO Strategy</TabsTrigger>
          <TabsTrigger value="business" data-testid="settings-tab-business">Business Profile</TabsTrigger>
          <TabsTrigger value="team" data-testid="settings-tab-team">Team</TabsTrigger>
          <TabsTrigger value="account" data-testid="settings-tab-account">Account</TabsTrigger>
          <TabsTrigger value="danger" data-testid="settings-tab-danger">Danger Zone</TabsTrigger>
        </TabsList>

        <TabsContent value="strategy">
          {!strategy ? <p className="text-sm text-muted-foreground">Select a website first.</p> : (
            <Card className="shadow-none">
              <CardContent className="space-y-4 p-5">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-1.5"><Label className="text-xs">Primary country</Label>
                    <Input value={strategy.primary_country || ""} onChange={(e) => setStrategy({ ...strategy, primary_country: e.target.value })} className="h-8 text-xs" data-testid="strategy-country-input" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Language</Label>
                    <Input value={strategy.language || ""} onChange={(e) => setStrategy({ ...strategy, language: e.target.value })} className="h-8 text-xs" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Business type</Label>
                    <Input value={strategy.business_type || ""} onChange={(e) => setStrategy({ ...strategy, business_type: e.target.value })} className="h-8 text-xs" /></div>
                </div>
                <TagEditor label="Services" values={strategy.services || []} onChange={(v) => setStrategy({ ...strategy, services: v })} placeholder="e.g. emergency repairs" testId="strategy-services-input" />
                <TagEditor label="Target locations" values={strategy.target_locations || []} onChange={(v) => setStrategy({ ...strategy, target_locations: v })} placeholder="e.g. Manchester" testId="strategy-locations-input" />
                <TagEditor label="Brand terms" values={strategy.brand_terms || []} onChange={(v) => setStrategy({ ...strategy, brand_terms: v })} placeholder="your brand name" testId="strategy-brand-input" />
                <TagEditor label="Competitors" values={strategy.competitors || []} onChange={(v) => setStrategy({ ...strategy, competitors: v })} placeholder="competitor.com" testId="strategy-competitors-input" />
                <TagEditor label="Conversion goals" values={strategy.conversion_goals || []} onChange={(v) => setStrategy({ ...strategy, conversion_goals: v })} placeholder="e.g. generate_lead" testId="strategy-goals-input" />
                <TagEditor label="Excluded URLs" values={strategy.excluded_urls || []} onChange={(v) => setStrategy({ ...strategy, excluded_urls: v })} placeholder="https://…" testId="strategy-excluded-input" />
                <TagEditor label="Protected URLs (AI must never modify)" values={strategy.protected_urls || []} onChange={(v) => setStrategy({ ...strategy, protected_urls: v })} placeholder="https://…/pricing" testId="strategy-protected-input" />
                <Button onClick={saveStrategy} disabled={busy === "strategy"} data-testid="strategy-save-button">
                  {busy === "strategy" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save strategy
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="business">
          {!profile ? <p className="text-sm text-muted-foreground">Select a website first.</p> : (
            <Card className="shadow-none">
              <CardContent className="space-y-4 p-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  {[["businessName", "Business name"], ["legalName", "Legal name"], ["email", "Email"], ["phone", "Phone"], ["city", "City"], ["region", "Region/State"], ["country", "Country"]].map(([k, l]) => (
                    <div key={k} className="space-y-1.5">
                      <Label className="text-xs">{l}</Label>
                      <Input value={profile[k] || ""} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} className="h-8 text-xs" data-testid={`business-${k}-input`} />
                    </div>
                  ))}
                </div>
                <TagEditor label="Service areas" values={profile.serviceAreas || []} onChange={(v) => setProfile({ ...profile, serviceAreas: v })} placeholder="e.g. Greater Manchester" testId="business-service-areas-input" />
                <div className="space-y-1.5">
                  <Label className="text-xs">Business description</Label>
                  <Textarea value={profile.businessDescription || ""} onChange={(e) => setProfile({ ...profile, businessDescription: e.target.value })} data-testid="business-description-input" />
                </div>
                <Button onClick={saveProfile} disabled={busy === "profile"} data-testid="business-save-button">
                  {busy === "profile" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save profile
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="team">
          <Card className="shadow-none">
            <CardContent className="space-y-4 p-5">
              <div className="space-y-2">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-md border p-2" data-testid="team-member-row">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{m.name}</div>
                      <div className="text-xs text-muted-foreground">{m.email}</div>
                    </div>
                    <Badge variant="outline" className="ml-auto text-xs capitalize">{m.role}</Badge>
                    {m.role !== "owner" && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => save("rm", async () => {
                        await api.delete(`/workspaces/${activeWorkspace.id}/members/${m.id}`);
                        setMembers(members.filter((x) => x.id !== m.id));
                      }, "Member removed")} data-testid="team-remove-member"><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Input value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="member@email.com" className="h-8 w-56 text-xs" data-testid="team-invite-email-input" />
                <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                  <SelectTrigger className="h-8 w-28 text-xs" data-testid="team-invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["viewer", "member", "admin"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="h-8" onClick={addMember} disabled={!invite.email || busy === "invite"} data-testid="team-invite-button">Add member</Button>
              </div>
              <InfoNote>The person must already have a SIX9 SEO account (email invitations arrive with the email provider integration).</InfoNote>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account">
          <Card className="shadow-none">
            <CardContent className="max-w-md space-y-4 p-5">
              <div className="space-y-1.5"><Label className="text-xs">Name</Label>
                <Input value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} data-testid="account-name-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Current password (only to change password)</Label>
                <Input type="password" value={account.current_password} onChange={(e) => setAccount({ ...account, current_password: e.target.value })} data-testid="account-current-password-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">New password</Label>
                <Input type="password" value={account.new_password} onChange={(e) => setAccount({ ...account, new_password: e.target.value })} data-testid="account-new-password-input" /></div>
              <Button onClick={saveAccount} disabled={busy === "account"} data-testid="account-save-button">
                {busy === "account" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save account
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="danger">
          <Card className="border-rose-200 shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-rose-700">Danger Zone</CardTitle></CardHeader>
            <CardContent className="space-y-3 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-rose-200 p-3">
                <div>
                  <div className="text-sm font-medium">Delete website</div>
                  <div className="text-xs text-muted-foreground">Removes {activeWebsite?.domain || "—"} and ALL its data: pages, metrics, opportunities, tasks, deployments.</div>
                </div>
                <Button variant="destructive" size="sm" disabled={!activeWebsite} onClick={() => { setDelOpen("website"); setConfirmName(""); }} data-testid="danger-delete-website-button">Delete website</Button>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-rose-200 p-3">
                <div>
                  <div className="text-sm font-medium">Delete workspace</div>
                  <div className="text-xs text-muted-foreground">Removes {activeWorkspace?.name} with every website, connection and record inside it. Owner only.</div>
                </div>
                <Button variant="destructive" size="sm" onClick={() => { setDelOpen("workspace"); setConfirmName(""); }} data-testid="danger-delete-workspace-button">Delete workspace</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!delOpen} onOpenChange={(o) => !o && setDelOpen(null)}>
        <DialogContent data-testid="danger-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm deletion</DialogTitle>
            <DialogDescription>
              This permanently deletes the {delOpen} and all associated data. Type{" "}
              <span className="font-mono font-semibold">{delOpen === "website" ? activeWebsite?.domain : activeWorkspace?.name}</span> to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder="Type to confirm" data-testid="danger-confirm-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={busy === "delete"} data-testid="danger-confirm-button">
              {busy === "delete" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Permanently delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
